import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { ReactGanttRef } from '@dhx/react-gantt';
import type { TaskSnapshot } from '../../../../stores/types';
import {
  TARGETS_CELL_MIN_WIDTH,
  TARGETS_HEADER_HEIGHT,
  TARGETS_PERIOD_SUBCOLUMNS,
  type TargetsPeriodColumn,
} from './targetsPeriodUtils';
import {
  isMultiCellMatrix,
  isTypingTarget,
  normalizeSelection,
  parseClipboardQtyMatrix,
  resolvePasteMatrix,
  selectionContains,
  serializeQtyMatrix,
  type TargetsSelection,
} from './targetsClipboard';
import { useGanttTargetsScrollSync } from './useGanttTargetsScrollSync';
import { getPrimaryUnitActivityId, type PeriodQuantity } from './useTaskTargetsData';
import { effectiveUom } from '../../../../gantt/effectiveUom';

export interface GanttTargetsPanelProps {
  ganttRef: React.RefObject<ReactGanttRef | null>;
  /** Visible rows in Gantt display order; isNonLeaf must match DHTMLX grid_row_class. */
  visibleRows: Array<{ id: string; isNonLeaf: boolean }>;
  getTask: (id: string) => TaskSnapshot | undefined;
  /** Leaf tasks in the look-ahead window — only these can be edited. */
  lookAheadLeafTaskIds: Set<string>;
  rowHeight: number;
  columns: TargetsPeriodColumn[];
  canEdit: boolean;
  getCellValue: (unitActivityId: string | null, column: TargetsPeriodColumn) => PeriodQuantity;
  setCellTarget: (unitActivityId: string, column: TargetsPeriodColumn, value: number | null) => void;
  /** Batch draft updates for multi-cell paste. */
  setCellTargets: (
    updates: Array<{
      unitActivityId: string;
      column: TargetsPeriodColumn;
      value: number | null;
    }>,
  ) => void;
  isCellDirty: (unitActivityId: string | null, column: TargetsPeriodColumn) => boolean;
}

/** Rows above/below viewport to keep mounted while scrolling. */
const ROW_OVERSCAN = 12;

const SUBCOL_BORDER = '1px solid var(--gantt-cell-border, var(--mantine-color-default-border))';
const PERIOD_BORDER = '1px solid var(--gantt-cell-border, var(--mantine-color-default-border))';

function formatQty(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '';
  return String(n);
}

/** Allow digits + one decimal; reject negatives and junk. */
function parseNonNegativeQty(raw: string): number | null | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d*\.?\d*$/.test(trimmed)) return 'invalid';
  if (trimmed === '.' || trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return 'invalid';
  return n;
}

function SubCell({
  children,
  title,
  muted,
  dirty,
  selected,
  onMouseDown,
  onMouseEnter,
}: {
  children: React.ReactNode;
  title?: string;
  muted?: boolean;
  dirty?: boolean;
  selected?: boolean;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      title={title}
      className={[
        muted ? 'fbt-gantt-targets-subcell is-actual' : 'fbt-gantt-targets-subcell is-planned',
        selected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      style={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 2px',
        background: selected
          ? 'color-mix(in srgb, var(--mantine-color-brand-5) 28%, transparent)'
          : dirty
            ? 'color-mix(in srgb, var(--mantine-color-yellow-4) 22%, transparent)'
            : muted
              ? 'color-mix(in srgb, var(--mantine-color-gray-4) 10%, transparent)'
              : undefined,
        outline: selected ? '1px solid var(--mantine-color-brand-6)' : undefined,
        outlineOffset: -1,
        fontVariantNumeric: 'tabular-nums',
        cursor: muted ? undefined : 'cell',
      }}
    >
      {children}
    </div>
  );
}

function TargetCell({
  column,
  quantity,
  uom,
  editable,
  dirty,
  selected,
  disabledReason,
  onChange,
  onPlannedMouseDown,
  onPlannedMouseEnter,
}: {
  column: TargetsPeriodColumn;
  quantity: PeriodQuantity;
  /** Unit of measure shown next to Achieved qty */
  uom?: string | null;
  editable: boolean;
  dirty: boolean;
  selected: boolean;
  disabledReason?: string;
  /** null = discard draft / revert to saved */
  onChange: (value: number | null) => void;
  onPlannedMouseDown?: (e: React.MouseEvent) => void;
  onPlannedMouseEnter?: (e: React.MouseEvent) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState('');
  const skipBlurCommitRef = useRef(false);
  /** Last value this input emitted — used to detect external updates (paste) while focused. */
  const lastEmittedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!focused) {
      setText(formatQty(quantity.target));
      lastEmittedRef.current = quantity.target;
      return;
    }
    // Paste/batch updated this cell while the input still has focus.
    if (lastEmittedRef.current !== quantity.target) {
      setText(formatQty(quantity.target));
      lastEmittedRef.current = quantity.target;
    }
  }, [quantity.target, focused]);

  const plannedDisplay = formatQty(quantity.target);
  const achievedQty = formatQty(quantity.achieved) || '—';
  const uomLabel = (uom ?? '').trim();
  const dimmed = !editable && disabledReason && !disabledReason.startsWith('Summary');

  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        gridTemplateColumns: TARGETS_PERIOD_SUBCOLUMNS,
        opacity: dimmed ? 0.55 : 1,
        background:
          column.isPast || column.isNonWorking
            ? 'color-mix(in srgb, var(--mantine-color-gray-4) 12%, transparent)'
            : undefined,
      }}
    >
      <div style={{ borderRight: SUBCOL_BORDER, minWidth: 0, height: '100%' }}>
        <SubCell
          title={
            dirty
              ? 'Unsaved change — Save look ahead to persist'
              : disabledReason
          }
          dirty={dirty}
          selected={selected}
          onMouseDown={onPlannedMouseDown}
          onMouseEnter={onPlannedMouseEnter}
        >
          {editable ? (
            <TextInput
              size="xs"
              variant="unstyled"
              inputMode="decimal"
              value={focused ? text : plannedDisplay}
              aria-label="Planned target"
              styles={{
                root: { width: '100%' },
                input: {
                  height: 22,
                  minHeight: 22,
                  textAlign: 'center',
                  fontSize: 12,
                  padding: 0,
                  fontWeight: dirty ? 600 : 500,
                  fontVariantNumeric: 'tabular-nums',
                },
              }}
              onFocus={() => {
                setFocused(true);
                setText(formatQty(quantity.target));
                lastEmittedRef.current = quantity.target;
              }}
              onBlur={() => {
                if (skipBlurCommitRef.current) {
                  skipBlurCommitRef.current = false;
                  setFocused(false);
                  return;
                }
                const parsed = parseNonNegativeQty(text);
                if (parsed === 'invalid') {
                  setText(formatQty(quantity.target));
                } else if (parsed === null) {
                  lastEmittedRef.current = null;
                  onChange(null);
                } else {
                  lastEmittedRef.current = parsed;
                  onChange(parsed);
                }
                setFocused(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  skipBlurCommitRef.current = true;
                  lastEmittedRef.current = null;
                  onChange(null);
                  setFocused(false);
                  (e.currentTarget as HTMLInputElement).blur();
                  return;
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
              onChange={(e) => {
                const raw = e.currentTarget.value;
                if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                setText(raw);
                const parsed = parseNonNegativeQty(raw);
                if (parsed === 'invalid') return;
                if (parsed === null) {
                  lastEmittedRef.current = null;
                  onChange(null);
                  return;
                }
                lastEmittedRef.current = parsed;
                onChange(parsed);
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color:
                  plannedDisplay || quantity.achieved > 0
                    ? 'var(--mantine-color-text)'
                    : 'var(--mantine-color-dimmed)',
              }}
            >
              {plannedDisplay || (quantity.achieved > 0 ? '0' : '—')}
            </span>
          )}
        </SubCell>
      </div>
      <SubCell
        muted
        title={
          uomLabel
            ? `Achieved: ${formatQty(quantity.achieved) || '0'} ${uomLabel}`
            : `Achieved: ${formatQty(quantity.achieved) || '0'}`
        }
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            gap: 3,
            maxWidth: '100%',
            overflow: 'hidden',
            fontSize: 12,
            fontVariantNumeric: 'tabular-nums',
            color: quantity.achieved > 0 ? 'var(--mantine-color-text)' : 'var(--mantine-color-dimmed)',
          }}
        >
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {achievedQty}
          </span>
          {uomLabel && achievedQty !== '—' ? (
            <span
              style={{
                fontSize: 10,
                lineHeight: 1,
                color: 'var(--mantine-color-dimmed)',
                flexShrink: 0,
              }}
            >
              {uomLabel}
            </span>
          ) : null}
        </span>
      </SubCell>
    </div>
  );
}

type TargetsRowProps = {
  taskId: string;
  rowIndex: number;
  isNonLeaf: boolean;
  rowHeight: number;
  gridTemplateColumns: string;
  columns: TargetsPeriodColumn[];
  inLookAhead: boolean;
  canEdit: boolean;
  selection: TargetsSelection | null;
  getTask: (id: string) => TaskSnapshot | undefined;
  getCellValue: (unitActivityId: string | null, column: TargetsPeriodColumn) => PeriodQuantity;
  setCellTarget: (unitActivityId: string, column: TargetsPeriodColumn, value: number | null) => void;
  isCellDirty: (unitActivityId: string | null, column: TargetsPeriodColumn) => boolean;
  onPlannedMouseDown: (rowIndex: number, colIndex: number, e: React.MouseEvent) => void;
  onPlannedMouseEnter: (rowIndex: number, colIndex: number) => void;
};

const TargetsRow = memo(function TargetsRow({
  taskId,
  rowIndex,
  isNonLeaf,
  rowHeight,
  gridTemplateColumns,
  columns,
  inLookAhead,
  canEdit,
  selection,
  getTask,
  getCellValue,
  setCellTarget,
  isCellDirty,
  onPlannedMouseDown,
  onPlannedMouseEnter,
}: TargetsRowProps) {
  const task = getTask(taskId);
  const uaId = getPrimaryUnitActivityId(task);
  const uaCount = Array.isArray(task?.unitActivityDetails)
    ? task!.unitActivityDetails!.length
    : 0;
  const wbsUom = String(task?.unitActivityDetails?.[0]?.unitOfMeasure ?? '').trim();
  const uom = wbsUom || (task ? effectiveUom(task.id, getTask).uom : null);
  const tracked = uaId != null;
  const editableRow = Boolean(canEdit && !isNonLeaf && inLookAhead && tracked);
  const editBlockReason = isNonLeaf
    ? 'Summary tasks have no targets'
    : !inLookAhead
      ? 'Outside look-ahead window'
      : !tracked
        ? 'Track this task to set targets'
        : !canEdit
          ? 'You do not have permission to edit targets'
          : undefined;

  return (
    <div
      data-task-id={taskId}
      data-non-leaf={isNonLeaf ? '1' : '0'}
      data-look-ahead={inLookAhead ? '1' : '0'}
      data-editable={editableRow ? '1' : '0'}
      className={
        isNonLeaf ? 'fbt-gantt-targets-row is-non-leaf' : 'fbt-gantt-targets-row is-leaf'
      }
      style={{
        display: 'grid',
        gridTemplateColumns,
        height: rowHeight,
        boxSizing: 'border-box',
      }}
    >
      {columns.map((col, colIndex) => {
        const qty = getCellValue(uaId, col);
        const canEditCell = editableRow && !col.isPast && !col.isNonWorking;
        const disabledReason = !canEditCell
          ? isNonLeaf || !inLookAhead || !tracked || !canEdit
            ? editBlockReason
            : col.isPast
              ? 'Past periods are read-only'
              : col.isNonWorking
                ? 'Non-working day (weekend or holiday)'
                : undefined
          : undefined;

        return (
          <div
            key={col.key}
            className="fbt-gantt-targets-cell"
            style={{
              borderRight: PERIOD_BORDER,
              borderBottom: PERIOD_BORDER,
              height: '100%',
              boxSizing: 'border-box',
              position: 'relative',
            }}
            title={uaCount > 1 ? `${uaCount} unit activities (editing first)` : undefined}
          >
            <TargetCell
              column={col}
              quantity={qty}
              uom={uom}
              editable={canEditCell}
              dirty={canEditCell && isCellDirty(uaId, col)}
              selected={selectionContains(selection, rowIndex, colIndex)}
              disabledReason={disabledReason}
              onChange={(value) => {
                if (!uaId) return;
                setCellTarget(uaId, col, value);
              }}
              onPlannedMouseDown={(e) => onPlannedMouseDown(rowIndex, colIndex, e)}
              onPlannedMouseEnter={() => onPlannedMouseEnter(rowIndex, colIndex)}
            />
          </div>
        );
      })}
    </div>
  );
});

function canEditPlannedCell(opts: {
  canEdit: boolean;
  isNonLeaf: boolean;
  inLookAhead: boolean;
  tracked: boolean;
  column: TargetsPeriodColumn;
}): boolean {
  return Boolean(
    opts.canEdit &&
      !opts.isNonLeaf &&
      opts.inLookAhead &&
      opts.tracked &&
      !opts.column.isPast &&
      !opts.column.isNonWorking,
  );
}

export function GanttTargetsPanel({
  ganttRef,
  visibleRows,
  getTask,
  lookAheadLeafTaskIds,
  rowHeight,
  columns,
  canEdit,
  getCellValue,
  setCellTarget,
  setCellTargets,
  isCellDirty,
}: GanttTargetsPanelProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const syncingXRef = useRef(false);
  const selectingRef = useRef(false);
  const selectionRef = useRef<TargetsSelection | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  /** Body vertical scrollbar width — header must reserve the same so 1fr columns align. */
  const [bodyScrollbarWidth, setBodyScrollbarWidth] = useState(0);
  const [selection, setSelection] = useState<TargetsSelection | null>(null);
  selectionRef.current = selection;

  useGanttTargetsScrollSync(ganttRef, bodyRef, true);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const update = () => {
      setViewportHeight(el.clientHeight);
      // overlay scrollbars → 0; classic scrollbars → ~15px
      setBodyScrollbarWidth(Math.max(0, el.offsetWidth - el.clientWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visibleRows.length, columns.length, rowHeight]);

  const cellMinWidth = columns[0]
    ? TARGETS_CELL_MIN_WIDTH[columns[0].frequency]
    : TARGETS_CELL_MIN_WIDTH.DAY;

  const contentMinWidth = columns.length * cellMinWidth;

  const gridTemplateColumns = useMemo(
    () => columns.map(() => `minmax(${cellMinWidth}px, 1fr)`).join(' '),
    [columns, cellMinWidth],
  );

  const totalHeight = visibleRows.length * rowHeight;

  // Vertical window + keep period header horizontally aligned with the body.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const onScroll = () => {
      setScrollTop(el.scrollTop);
      if (syncingXRef.current) return;
      const header = headerScrollRef.current;
      if (header && header.scrollLeft !== el.scrollLeft) {
        syncingXRef.current = true;
        header.scrollLeft = el.scrollLeft;
        syncingXRef.current = false;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    setScrollTop(el.scrollTop);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const { startIndex, endIndex } = useMemo(() => {
    const safeRowHeight = Math.max(1, rowHeight);
    const vh = viewportHeight > 0 ? viewportHeight : safeRowHeight * 24;
    const start = Math.max(0, Math.floor(scrollTop / safeRowHeight) - ROW_OVERSCAN);
    const visibleCount = Math.ceil(vh / safeRowHeight) + 1;
    const end = Math.min(visibleRows.length, start + visibleCount + ROW_OVERSCAN * 2);
    return { startIndex: start, endIndex: end };
  }, [scrollTop, viewportHeight, rowHeight, visibleRows.length]);

  const windowRows = useMemo(
    () => visibleRows.slice(startIndex, endIndex),
    [visibleRows, startIndex, endIndex],
  );

  const onPlannedMouseDown = useCallback(
    (rowIndex: number, colIndex: number, e: React.MouseEvent) => {
      // Keep text-caret behavior inside the input; still update selection.
      if (e.button !== 0) return;
      selectingRef.current = true;
      const coord = { rowIndex, colIndex };
      setSelection((prev) => {
        if (e.shiftKey && prev) {
          return { anchor: prev.anchor, focus: coord };
        }
        return { anchor: coord, focus: coord };
      });
    },
    [],
  );

  const onPlannedMouseEnter = useCallback((rowIndex: number, colIndex: number) => {
    if (!selectingRef.current) return;
    setSelection((prev) => {
      if (!prev) return { anchor: { rowIndex, colIndex }, focus: { rowIndex, colIndex } };
      return { ...prev, focus: { rowIndex, colIndex } };
    });
  }, []);

  useEffect(() => {
    const onUp = () => {
      selectingRef.current = false;
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, []);

  const visibleRowIdsKey = useMemo(
    () => visibleRows.map((r) => r.id).join('\0'),
    [visibleRows],
  );
  const columnsKey = useMemo(() => columns.map((c) => c.key).join('|'), [columns]);

  // Drop stale selection when row order/ids or period columns change (not on scroll remounts).
  useEffect(() => {
    setSelection(null);
  }, [visibleRowIdsKey, columnsKey]);

  const buildCopyText = useCallback(() => {
    const sel = selectionRef.current;
    if (!sel || columns.length === 0) return null;
    const { rowStart, rowEnd, colStart, colEnd } = normalizeSelection(sel);
    const matrix: Array<Array<number | null>> = [];
    for (let r = rowStart; r <= rowEnd; r += 1) {
      const row = visibleRows[r];
      if (!row) continue;
      const task = getTask(row.id);
      const uaId = getPrimaryUnitActivityId(task);
      const line: Array<number | null> = [];
      for (let c = colStart; c <= colEnd; c += 1) {
        const col = columns[c];
        if (!col) {
          line.push(null);
          continue;
        }
        const v = getCellValue(uaId, col).target;
        line.push(Number.isFinite(v) ? v : null);
      }
      matrix.push(line);
    }
    if (matrix.length === 0) return null;
    return serializeQtyMatrix(matrix);
  }, [columns, visibleRows, getTask, getCellValue]);

  const pasteMatrix = useCallback(
    (matrix: Array<Array<number | null>>) => {
      const sel = selectionRef.current;
      if (!sel || matrix.length === 0 || columns.length === 0) return;
      const { rowStart, rowEnd, colStart, colEnd } = normalizeSelection(sel);
      const selRows = rowEnd - rowStart + 1;
      const selCols = colEnd - colStart + 1;
      // 1×1 → fill selection; otherwise clipboard size from selection top-left.
      const paste = resolvePasteMatrix(matrix, selRows, selCols);
      // Flatten clipboard values; blocked cells do not consume the next value.
      const values: Array<number | null> = [];
      for (const line of paste) {
        for (const v of line) values.push(v);
      }
      if (values.length === 0) return;

      let attempted = 0;
      let applied = 0;
      let skipped = 0;
      let valueIndex = 0;
      const updates: Array<{
        unitActivityId: string;
        column: TargetsPeriodColumn;
        value: number | null;
      }> = [];

      const footprintRows = paste.length;
      const footprintCols = Math.max(...paste.map((r) => r.length), 0);

      for (let dr = 0; dr < footprintRows; dr += 1) {
        const rowIndex = rowStart + dr;
        const row = visibleRows[rowIndex];
        if (!row) break;
        const task = getTask(row.id);
        const uaId = getPrimaryUnitActivityId(task);
        const tracked = uaId != null;
        const inLookAhead = lookAheadLeafTaskIds.has(row.id);

        for (let dc = 0; dc < footprintCols; dc += 1) {
          if (valueIndex >= values.length) break;
          const colIndex = colStart + dc;
          const col = columns[colIndex];
          if (!col) break;
          attempted += 1;
          const editable = canEditPlannedCell({
            canEdit,
            isNonLeaf: row.isNonLeaf,
            inLookAhead,
            tracked,
            column: col,
          });
          if (!editable || !uaId) {
            skipped += 1;
            continue;
          }
          const raw = values[valueIndex]!;
          valueIndex += 1;
          // Empty clipboard cell → clear draft (revert to saved), not "set 0".
          updates.push({ unitActivityId: uaId, column: col, value: raw == null ? null : raw });
          applied += 1;
        }
        if (valueIndex >= values.length) break;
      }

      if (attempted === 0) return;
      setCellTargets(updates);
      notifications.show({
        title: 'Paste look ahead',
        message:
          skipped > 0
            ? `Pasted ${applied} of ${attempted} cells (${skipped} skipped — read-only or blocked).`
            : `Pasted ${applied} cell${applied === 1 ? '' : 's'}.`,
        color: applied > 0 ? 'teal' : 'gray',
      });
    },
    [columns, visibleRows, getTask, lookAheadLeafTaskIds, canEdit, setCellTargets],
  );

  useEffect(() => {
    const panel = bodyRef.current?.closest('.fbt-gantt-targets-panel') as HTMLElement | null;
    if (!panel) return;

    const onCopy = (e: ClipboardEvent) => {
      if (!panel.contains(e.target as Node)) return;
      // Let native copy win when the user selected text inside an input.
      if (isTypingTarget(e.target)) {
        const input = e.target as HTMLInputElement;
        if (
          typeof input.selectionStart === 'number' &&
          typeof input.selectionEnd === 'number' &&
          input.selectionStart !== input.selectionEnd
        ) {
          return;
        }
      }
      if (!selectionRef.current) return;
      const text = buildCopyText();
      if (text == null) return;
      e.preventDefault();
      e.clipboardData?.setData('text/plain', text);
      // Mirror into the async clipboard API when available (paste outside the app).
      void navigator.clipboard?.writeText(text).catch(() => undefined);
      notifications.show({
        title: 'Copied',
        message: 'Planned quantities copied. Paste into Look ahead or Excel.',
        color: 'gray',
      });
    };

    const onPaste = (e: ClipboardEvent) => {
      if (!panel.contains(e.target as Node)) return;
      const text = e.clipboardData?.getData('text/plain') ?? '';
      const matrix = parseClipboardQtyMatrix(text);
      if (matrix.length === 0) return;

      const sel = selectionRef.current;
      const multiSelection = (() => {
        if (!sel) return false;
        const { rowStart, rowEnd, colStart, colEnd } = normalizeSelection(sel);
        return rowStart !== rowEnd || colStart !== colEnd;
      })();

      // Single focused input + 1×1 clipboard: keep native typing paste.
      if (isTypingTarget(e.target) && !multiSelection && !isMultiCellMatrix(matrix)) {
        return;
      }
      // Need an anchor cell for grid paste.
      if (!sel) return;

      e.preventDefault();
      pasteMatrix(matrix);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!panel.contains(e.target as Node) && document.activeElement !== panel) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'c' || e.key === 'C')) {
        // copy handled via 'copy' event
        return;
      }
      if (mod && (e.key === 'v' || e.key === 'V')) {
        // paste handled via 'paste' event
        return;
      }
      if (e.key === 'Escape' && selectionRef.current && !isTypingTarget(e.target)) {
        setSelection(null);
      }
    };

    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [buildCopyText, pasteMatrix]);

  return (
    <Box
      className="fbt-gantt-targets-panel"
      tabIndex={0}
      title="Select Planned cells, then Ctrl/Cmd+C / Ctrl/Cmd+V to copy or paste"
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 'none',
        background: 'var(--mantine-color-body)',
        outline: 'none',
      }}
    >
      {/* Header outside the Y-scroll body so row scrollTop matches Gantt 1:1. */}
      <div
        ref={headerScrollRef}
        className="fbt-gantt-targets-header-scroll"
        style={{
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          // Hide header scrollbar; body owns horizontal scrolling UX.
          scrollbarWidth: 'none',
          // Match body vertical scrollbar gutter so period columns stay aligned.
          paddingRight: bodyScrollbarWidth,
          boxSizing: 'border-box',
        }}
        onScroll={() => {
          if (syncingXRef.current) return;
          const header = headerScrollRef.current;
          const body = bodyRef.current;
          if (!header || !body || body.scrollLeft === header.scrollLeft) return;
          syncingXRef.current = true;
          body.scrollLeft = header.scrollLeft;
          syncingXRef.current = false;
        }}
      >
        <div
          className="fbt-gantt-targets-header"
          style={{
            minWidth: contentMinWidth,
            width: '100%',
            display: 'grid',
            gridTemplateColumns,
            height: TARGETS_HEADER_HEIGHT,
            borderBottom: '1px solid var(--gantt-cell-border, var(--mantine-color-default-border))',
            background: 'var(--mantine-color-default-hover, #f1f3f5)',
            boxSizing: 'border-box',
          }}
        >
          {columns.map((col) => (
            <div
              key={col.key}
              className="fbt-gantt-targets-period-header"
              title={
                col.isNonWorking
                  ? 'Non-working day (weekend or holiday)'
                  : col.isPast
                    ? 'Past period'
                    : undefined
              }
              style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                borderRight: PERIOD_BORDER,
                background:
                  col.isPast || col.isNonWorking
                    ? 'color-mix(in srgb, var(--mantine-color-gray-4) 14%, transparent)'
                    : undefined,
              }}
            >
              {/* Parent: period label spans both sub-columns */}
              <div
                style={{
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2px 4px',
                  borderBottom: SUBCOL_BORDER,
                  minHeight: 0,
                }}
              >
                <Text size="xs" fw={700} style={{ lineHeight: 1.15 }} c={col.isNonWorking ? 'dimmed' : undefined}>
                  {col.label}
                </Text>
                {col.subLabel ? (
                  <Text size="xs" c="dimmed" style={{ lineHeight: 1.15, fontSize: 10 }}>
                    {col.subLabel}
                  </Text>
                ) : null}
              </div>
              {/* Children: Planned | Achieved (abbreviated on Day scale) */}
              <div
                style={{
                  flex: '0 0 20px',
                  display: 'grid',
                  gridTemplateColumns: TARGETS_PERIOD_SUBCOLUMNS,
                  height: 20,
                }}
              >
                <div
                  title="Planned"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRight: SUBCOL_BORDER,
                    minWidth: 0,
                    padding: '0 2px',
                  }}
                >
                  <Text
                    size="xs"
                    c="dimmed"
                    fw={600}
                    style={{
                      fontSize: col.frequency === 'DAY' ? 10 : 9,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.frequency === 'DAY' ? 'P' : 'Planned'}
                  </Text>
                </div>
                <div
                  title="Achieved"
                  className="fbt-gantt-targets-subcell is-actual"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'color-mix(in srgb, var(--mantine-color-gray-4) 10%, transparent)',
                    minWidth: 0,
                    padding: '0 2px',
                  }}
                >
                  <Text
                    size="xs"
                    c="dimmed"
                    fw={600}
                    style={{
                      fontSize: col.frequency === 'DAY' ? 10 : 9,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.frequency === 'DAY' ? 'A' : 'Achieved'}
                  </Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        ref={bodyRef}
        className="fbt-gantt-targets-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          // Prefer stable gutter when supported; measured padding on header covers the rest.
          scrollbarGutter: 'stable',
        }}
      >
        <div
          style={{
            position: 'relative',
            minWidth: contentMinWidth,
            width: '100%',
            height: totalHeight,
            boxSizing: 'border-box',
          }}
        >
          {windowRows.map((row, i) => {
            const index = startIndex + i;
            const taskId = row.id;
            return (
              <div
                key={taskId}
                style={{
                  position: 'absolute',
                  top: index * rowHeight,
                  left: 0,
                  right: 0,
                  height: rowHeight,
                }}
              >
                <TargetsRow
                  taskId={taskId}
                  rowIndex={index}
                  isNonLeaf={row.isNonLeaf}
                  rowHeight={rowHeight}
                  gridTemplateColumns={gridTemplateColumns}
                  columns={columns}
                  inLookAhead={lookAheadLeafTaskIds.has(taskId)}
                  canEdit={canEdit}
                  selection={selection}
                  getTask={getTask}
                  getCellValue={getCellValue}
                  setCellTarget={setCellTarget}
                  isCellDirty={isCellDirty}
                  onPlannedMouseDown={onPlannedMouseDown}
                  onPlannedMouseEnter={onPlannedMouseEnter}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Box>
  );
}
